/**
 * Centralized URL generation utilities.
 * This is the SOURCE OF TRUTH for URL structure across the application.
 *
 * This file is exported from studio to frontend to ensure consistency.
 * Used by:
 * - Sanity Studio resolver (locations and presentation tool)
 * - Frontend routing (pages/posts/guides)
 * - Sitemap generation
 */

/* eslint-disable no-restricted-imports */
import { defaultLanguage, type Language } from './languages'
/* eslint-enable no-restricted-imports */

export type DocumentType = 'page' | 'post' | 'guide' | 'home'

export interface UrlParams {
  type: DocumentType
  slug?: string
  language?: Language | string
}

/**
 * Generates the path (without domain) for a document.
 *
 * @example
 * buildDocumentPath({ type: 'page', slug: 'about', language: 'en' })
 * // Returns: '/about'
 *
 * @example
 * buildDocumentPath({ type: 'page', slug: 'about', language: 'es' })
 * // Returns: '/es/about'
 *
 * @example
 * buildDocumentPath({ type: 'post', slug: 'my-post', language: 'es' })
 * // Returns: '/es/posts/my-post'
 *
 * @example
 * buildDocumentPath({ type: 'guide', slug: 'my-guide', language: 'es' })
 * // Returns: '/es/guides/my-guide'
 */
export function buildDocumentPath({ type, slug, language = defaultLanguage }: UrlParams): string {
  // Determine if we need a language prefix (non-default languages get a prefix)
  const langPrefix = language && language !== defaultLanguage ? `/${language}` : ''

  switch (type) {
    case 'home':
      return langPrefix || '/'
    case 'post':
      return slug ? `${langPrefix}/posts/${slug}` : `${langPrefix}/posts`
    case 'guide':
      return slug ? `${langPrefix}/guides/${slug}` : `${langPrefix}/guides`
    case 'page':
      return slug ? `${langPrefix}/${slug}` : langPrefix || '/'
    default:
      console.warn('Invalid document type:', type)
      return '/'
  }
}

/**
 * Generates the full URL (with domain) for a document.
 *
 * @example
 * buildDocumentUrl('https://example.com', { type: 'page', slug: 'about', language: 'es' })
 * // Returns: 'https://example.com/es/about'
 */
export function buildDocumentUrl(baseUrl: string, params: UrlParams): string {
  const path = buildDocumentPath(params)
  return `${baseUrl}${path}`
}

/**
 * Extracts document information from a path.
 * Useful for reverse-resolving paths to document types.
 *
 * @example
 * parseDocumentPath('/es/posts/my-post')
 * // Returns: { type: 'post', slug: 'my-post', language: 'es' }
 */
export function parseDocumentPath(path: string): Partial<UrlParams> | null {
  // Remove trailing slash
  const normalizedPath = path.replace(/\/$/, '') || '/'

  // Check for locale prefix
  const localeMatch = normalizedPath.match(/^\/([a-z]{2})(\/|$)/)
  const language = localeMatch ? (localeMatch[1] as Language) : defaultLanguage
  const pathWithoutLocale = localeMatch ? normalizedPath.slice(3) : normalizedPath

  // Home page
  if (!pathWithoutLocale || pathWithoutLocale === '/') {
    return { type: 'home', language }
  }

  // Posts
  const postMatch = pathWithoutLocale.match(/^\/posts\/([^/]+)$/)
  if (postMatch) {
    return { type: 'post', slug: postMatch[1], language }
  }

  // Guides
  const guideMatch = pathWithoutLocale.match(/^\/guides\/([^/]+)$/)
  if (guideMatch) {
    return { type: 'guide', slug: guideMatch[1], language }
  }

  // Pages
  const pageMatch = pathWithoutLocale.match(/^\/([^/]+)$/)
  if (pageMatch) {
    return { type: 'page', slug: pageMatch[1], language }
  }

  return null
}
